import { useCallback, useRef, useState } from 'react';

/**
 * Per-FILE upload progress for the attachment rows (2026-09-14). Owner: *"can we
 * add an uploading green bar … with a %"*.
 *
 * `begin(files, group)` registers the files and returns a tracker:
 *   onProgress(fraction) — the REQUEST's 0…1, spread across its files by size,
 *                          because one multipart request carries every file;
 *   succeed()            — the files landed: their rows are removed (the caller
 *                          refreshes first, so the normal rows replace them);
 *   fail(message)        — the bar goes; the row keeps the file NAME and the
 *                          server's MESSAGE until `dismiss(key)`.
 *
 * `entriesIn(group)` lists `{ key, file, fraction, error }` for one surface —
 * `'attachment'`, `'delivery'`, or `item:<id>` — and `progressFor(file)` answers
 * for a STAGED `File` a caller already renders (null when it is not uploading).
 *
 * 🔑 IT HOLDS NO NETWORK LOGIC. The transport is `utils/api.js#apiUploadWithProgress`
 * via `utils/orderFileMutations.js#uploadOrderFiles`; this only remembers what to draw.
 */
export default function useUploadProgress() {
  const [entries, setEntries] = useState([]);
  const seq = useRef(0);

  const patch = useCallback((keys, change) => {
    setEntries((prev) => prev.map((e) => (keys.includes(e.key) ? { ...e, ...change(e) } : e)));
  }, []);

  const begin = useCallback((files, group) => {
    const list = Array.from(files || []);
    const added = list.map((file) => {
      seq.current += 1;
      return { key: `upload-${seq.current}`, file, group, fraction: 0, error: null };
    });
    const keys = added.map((e) => e.key);
    setEntries((prev) => [...prev, ...added]);

    const sizes = list.map((f) => Math.max(0, Number(f && f.size) || 0));
    const totalBytes = sizes.reduce((a, b) => a + b, 0);
    const onProgress = (fraction) => {
      const sent = Math.min(1, Math.max(0, fraction)) * totalBytes;
      let offset = 0;
      const perFile = sizes.map((size) => {
        const value = totalBytes === 0 ? fraction : (size === 0 ? (sent >= offset ? 1 : 0) : (sent - offset) / size);
        offset += size;
        return Math.min(1, Math.max(0, value));
      });
      setEntries((prev) => prev.map((e) => {
        const i = keys.indexOf(e.key);
        return i === -1 ? e : { ...e, fraction: Math.max(e.fraction, perFile[i]) };
      }));
    };
    return {
      onProgress,
      succeed: () => setEntries((prev) => prev.filter((e) => !keys.includes(e.key))),
      fail: (message) => patch(keys, () => ({ error: message || 'Upload failed' })),
    };
  }, [patch]);

  const dismiss = useCallback((key) => {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  }, []);

  const entriesIn = useCallback((group) => entries.filter((e) => e.group === group), [entries]);

  const progressFor = useCallback((file) => {
    const hit = entries.find((e) => e.file === file && !e.error);
    return hit ? hit.fraction : null;
  }, [entries]);

  return { begin, dismiss, entriesIn, progressFor };
}
